import { Injectable } from '@angular/core';
import { HttpService } from '../../shared/service/http.service';
import { ArgosState } from '../../core/argos-state';
import { NgRedux } from '@angular-redux/store';
import { FindHttpError } from '../../shared/error/find-http-error';
import { Observable } from 'rxjs/Observable';
import { HttpError } from '../../shared/error/http-error';
import { Subject } from 'rxjs/Subject';
import { Attending } from '../model/attending';
import { UpdateHttpError } from '../../shared/error/update-http-error';
import { AccessoryAmountService } from '../../shared/service/accessory-amount.service';
import { DeleteHttpError } from '../../shared/error/delete-http-error';
import { PageInfo } from '../../shared/model/page-info';
import AttendingInfo from '../model/attending-info';
import * as moment from 'moment';
import { AttendingFilter } from '../../shared/filter/attending-filter';
import { AuthenticationService } from '../../shared/service/authentication.service';
import { AttendingConversion } from '../model/attending-conversion';
import { SaveHttpError } from '../../shared/error/save-http-error';
import { InstallationPeriod } from '../constants/installation-period';
import { BooleanValue } from "../../shared/model/boolean-value";
import { Monitoring } from "../model/monitoring";
import { AttendingScore } from "../model/attending-score";
import { EquipmentSimCardUpdate } from "../model/equipment-sim-card-update";

@Injectable()
export class AttendingService {
  private static ATTENDING_END_POINT = '/attending';

  private FIND_BY_FILTER = `${AttendingService.ATTENDING_END_POINT}/findByFilter`;
  private SHOW_CURRENT = `${AttendingService.ATTENDING_END_POINT}/showCurrent`;
  private BUILD = `${AttendingService.ATTENDING_END_POINT}/build`;
  private CREATE = `${AttendingService.ATTENDING_END_POINT}/create`;
  private UPDATE = `${AttendingService.ATTENDING_END_POINT}/update`;
  private FINISH = `${AttendingService.ATTENDING_END_POINT}/finish`;
  private DELETE = `${AttendingService.ATTENDING_END_POINT}/delete`;
  private FINISH_MONITORING = `${AttendingService.ATTENDING_END_POINT}/finishMonitoring`;
  private FIND_PAGE_INFO = `${AttendingService.ATTENDING_END_POINT}/findPageInfo`;
  private ASSIGN_TO_USER = `${AttendingService.ATTENDING_END_POINT}/assign`;
  private UNASSIGN_TO_USER = `${AttendingService.ATTENDING_END_POINT}/unassign`;
  private ASSOCIATE_DIAGNOSIS = `${AttendingService.ATTENDING_END_POINT}/associateDiagnosis`;
  private FIND_ATTENDING_INFO = `${AttendingService.ATTENDING_END_POINT}/info`;
  private FIND_ATTENDING_KIT = `${AttendingService.ATTENDING_END_POINT}/kit`;
  private CONVERT_ATTENDING = `${AttendingService.ATTENDING_END_POINT}/convert`;
  private ATTENDING_STATUS = `${AttendingService.ATTENDING_END_POINT}/status`;
  private INFO_FILE = `${AttendingService.ATTENDING_END_POINT}/info-file`;
  private MONITORING_VALIDATION_STEP = `${AttendingService.ATTENDING_END_POINT}/monitoring/validation`;
  private MONITORING = `${AttendingService.ATTENDING_END_POINT}/monitoring`;
  private SET_TECHNICIAN_SCORE = `${AttendingService.ATTENDING_END_POINT}/score`;
  private RESCHEDULE_ATTENDING = `${AttendingService.ATTENDING_END_POINT}/reschedule`;
  private UPDATE_EQUIPMENT_INFO = `${AttendingService.ATTENDING_END_POINT}/equipment-info`;

  save$: Observable<any>;
  finish$: Observable<any>;
  create$: Observable<Attending>;
  build$: Observable<Attending>;
  current$: Observable<Attending>;
  find$: Observable<any>;
  findMovements$: Observable<any>;
  pageInfo$: Observable<PageInfo>;
  delete$: Observable<any>;
  error$: Observable<HttpError>;

  private saveSubject: Subject<any>;
  private finishSubject: Subject<any>;
  private createSubject: Subject<Attending>;
  private buildSubject: Subject<Attending>;
  private currentSubject: Subject<Attending>;
  private deleteSubject: Subject<Attending>;
  private pageInfoSubject: Subject<PageInfo>;
  private findSubject: Subject<Array<Attending> | Attending>;
  private findMovementsSubject: Subject<Array<any>>;
  private errorSubject: Subject<HttpError>;

  constructor(private _httpService: HttpService,
    private _authService: AuthenticationService,
    private _accessoryAmountService: AccessoryAmountService,
    private _ngRedux: NgRedux<ArgosState>) {
    this.saveSubject = new Subject();
    this.save$ = this.saveSubject.asObservable();
    this.finishSubject = new Subject();
    this.finish$ = this.finishSubject.asObservable();
    this.createSubject = new Subject();
    this.create$ = this.createSubject.asObservable();
    this.currentSubject = new Subject();
    this.current$ = this.currentSubject.asObservable();
    this.buildSubject = new Subject();
    this.build$ = this.buildSubject.asObservable();
    this.findSubject = new Subject();
    this.find$ = this.findSubject.asObservable();
    this.findMovementsSubject = new Subject();
    this.findMovements$ = this.findMovementsSubject.asObservable();
    this.pageInfoSubject = new Subject();
    this.pageInfo$ = this.pageInfoSubject.asObservable();
    this.deleteSubject = new Subject();
    this.delete$ = this.deleteSubject.asObservable();
    this.errorSubject = new Subject();
    this.error$ = this.errorSubject.asObservable();
  }

  create() {
    this._httpService.get(this.CREATE, Attending, FindHttpError)
      .subscribe(
        attending => this.createSubject.next(attending),
        error => this.errorSubject.next(error)
      );
  }

  updateAttending(data) {
    this._httpService.put(this.UPDATE, data.attending, Attending, UpdateHttpError)
      .subscribe(
        (attending) => this.saveSubject.next(attending),
        error => this.errorSubject.next(error)
      );
  }

  saveConfiguration(attending) {
    if (attending.configuration.simCard || attending.configuration.simCard.iccid) {
      this.updateAttending({ attending });
    } else {
      this.errorSubject.next({ text: 'Preencha a configuração corretamente.' });
    }
  }

  saveDispatch(attending) {
    this.updateAttending({ attending });
  }

  saveScheduling(attending) {
    this.updateAttending({ attending });
  }

  finishMonitoring(finishMonitoringDto) {
    return this._httpService.put(this.FINISH_MONITORING, finishMonitoringDto, Attending, UpdateHttpError)
      .subscribe(
        () => this.finishSubject.next(),
        error => this.errorSubject.next(error)
      );
  }

  saveValidation(data) {
    this._httpService.put(this.FINISH, data.attending, Attending, UpdateHttpError)
      .subscribe(
        () => this.finishSubject.next(),
        error => this.errorSubject.next(error)
      );
  }

  build(id: number) {
    this._httpService.get(`${this.BUILD}/${id}`, Attending, FindHttpError)
      .subscribe(
        attending => this.buildSubject.next(attending),
        error => this.errorSubject.next(error)
      );
  }

  convertAttending(id: number, conversion: AttendingConversion) {
    this._httpService.post(`${this.CONVERT_ATTENDING}/${id}`, conversion, Attending, SaveHttpError)
      .subscribe(
        attending => this.saveSubject.next(attending),
        error => this.errorSubject.next(error)
      );
  }

  assignMonitoringToUser(attendingId) {
    this._httpService.post(`${this.ASSIGN_TO_USER}/${attendingId}?stepType=MONITORING`, {}, Object, UpdateHttpError)
      .subscribe(
        () => { },
        error => {
          this.errorSubject.next(new UpdateHttpError('Não foi possível atribuir você para esse monitoramento.'));
        }
      );
  }

  unassignMonitoringToUser(attendingId) {
    this._httpService.post(`${this.UNASSIGN_TO_USER}/${attendingId}?stepType=MONITORING`, {}, Object, UpdateHttpError)
      .subscribe(
        () => { },
        error => {
          this.errorSubject.next(new UpdateHttpError('Não foi possível remover você do monitoramento.'));
        }
      );
  }

  showCurrent(id) {
    this._httpService.get(`${this.SHOW_CURRENT}/${id}`, Attending, FindHttpError)
      .subscribe(
        attending => this.currentSubject.next(attending),
        error => this.errorSubject.next(error)
      );
  }

  delete(id: number) {
    return this._httpService.delete(`${this.DELETE}/${id}`, Attending, DeleteHttpError);
  }

  findAttendings(pageNumber: number, pageSize: number) {
    this._ngRedux.select('attendingFilter')
      .subscribe((filter: AttendingFilter) => {
        let newFilter = { ...filter };
        this.assembleFilter(newFilter);
        this.findByFilter(newFilter, pageNumber, pageSize);
        this.findPageInfo(newFilter, pageSize);
      });
  }

  findAttendingInfo(id: number) {
    this._httpService.get(`${this.FIND_ATTENDING_INFO}/${id}`, AttendingInfo, FindHttpError)
      .subscribe(
        (attendingInfo) => this.findSubject.next(attendingInfo),
        (error) => { }
      );
  }

  findAttendingKit(attending) {
    this._httpService.post(this.FIND_ATTENDING_KIT, attending, Array, FindHttpError)
      .subscribe(
        (accessoryMovementLogs) => this.findMovementsSubject.next(accessoryMovementLogs),
        (error) => this.errorSubject.next(error)
      );
  }

  getVehicleParts(id: number, period: InstallationPeriod) {
    return this._httpService.get(`${AttendingService.ATTENDING_END_POINT}/${id}/vehicleparts?period=${period}`,
      Map, FindHttpError);
  }

  deleteInfoFile(id: number, attendingId: number) {
    return this._httpService.delete(`${this.INFO_FILE}/${id}?attendingId=${attendingId}`, BooleanValue, DeleteHttpError);
  }

  acceptInfoFile(id: number, accepted: boolean, attendingId: number) {
    return this._httpService.put(`${this.INFO_FILE}?accepted=${accepted}&attendingId=${attendingId}`, id, BooleanValue, UpdateHttpError);
  }

  updateStatus(id: number, status) {
    return this._httpService.put(`${this.ATTENDING_STATUS}?status=${status}`, id, BooleanValue, UpdateHttpError);
  }

  setMonitoringValidationStep(monitoring) {
    return this._httpService.post(`${this.MONITORING_VALIDATION_STEP}`, monitoring, BooleanValue, FindHttpError);
  }

  findMonitoring(id: number) {
    return this._httpService.get(`${this.MONITORING}/${id}`, Monitoring, FindHttpError);
  }

  setTechnicianScore(id: number, score: AttendingScore) {
    return this._httpService.post(`${this.SET_TECHNICIAN_SCORE}/${id}`, score, AttendingScore, SaveHttpError);
  }

  reschedule(id: number) {
    this._httpService.put(this.RESCHEDULE_ATTENDING, id, Attending, UpdateHttpError)
      .subscribe(
        () => this.finishSubject.next(),
        error => this.errorSubject.next(error)
      );
  }

  private assembleFilter(newFilter) {
    if (newFilter.scheduleDate) {
      newFilter.scheduleDate = moment(newFilter.scheduleDate, 'DD/MM/YYYY');
    }
    if (newFilter.monitoringUser) {
      newFilter.monitoringUser = this._authService.getUserFromLocalStorage();
    } else {
      newFilter.monitoringUser = null;
    }
    return newFilter;
  }

  private findByFilter(filter, pageNumber, pageSize) {
    this._httpService.post(`${this.FIND_BY_FILTER}?pageNumber=${pageNumber}&pageSize=${pageSize}`,
      filter, Array, FindHttpError)
      .subscribe(
        attendings => this.findSubject.next(attendings),
        error => this.errorSubject.next(error)
      );
  }

  private findPageInfo(filter, pageSize) {
    this._httpService.post(`${this.FIND_PAGE_INFO}?pageSize=${pageSize}`, filter, PageInfo, FindHttpError)
      .subscribe(
        pageInfo => this.pageInfoSubject.next(pageInfo),
        error => this.errorSubject.next(error)
      );
  }

  updateEquipmentInfo(id: number, equipmentSimCardUpdate: EquipmentSimCardUpdate) {
    return this._httpService.put(`${this.UPDATE_EQUIPMENT_INFO}/${id}`,
      equipmentSimCardUpdate,
      BooleanValue,
      UpdateHttpError)
  }

  getCanContinueMonitoringWorkFlow(id: number, period: InstallationPeriod) {
    return this._httpService.get(`${AttendingService.ATTENDING_END_POINT}/can-continue-monitoring-workflow/${id}?period=${period}`,
    BooleanValue, FindHttpError);
  }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/production-argos-front/argos/src/app/attending/service/attending.service.ts